import cn from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as React from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { api } from '@/api';
import { nl2br, textVariableReplacer } from '@/helpers/common';
import { usePageTitle } from '@/hooks/usePageTitle';
import usePublicTranslation from '@/hooks/usePublicTranslation';
import { GeneralSettings } from '@/types/setting';

import styles from './styles.module.scss';
import { TrustCenterData, TrustCenterMainData } from './types';

const dataAttributeTab = 'data-trust-center-tab';
const MAIN_DATA_URL = `/data/structure/group/view/organisation-manager/public/{dataStructureId}/{viewId}/trust-center/{trustCenterId}/main`;
const ACCESS_REQUEST_URL = `/data/structure/group/view/organisation-manager/public/{dataStructureId}/{viewId}/trust-center/{trustCenterId}/request-access`;
const CHECK_ACCESS_URL = `/data/structure/group/view/organisation-manager/public/{dataStructureId}/{viewId}/trust-center/{trustCenterId}/request-data`;

type TrustCenterCategoryWithId = TrustCenterData['categories'][number] & {
  id: string;
};

type TrustCenterDataWithCategoryId = Omit<TrustCenterData, 'categories'> & {
  categories: TrustCenterCategoryWithId[];
};

enum Page {
  RequestAccess = 'requestAccess',
  CheckAccess = 'checkAccess',
  AccessGranted = 'accessGranted'
}

type Props = {
  dataStructureId: string;
  viewId: string;
  className?: string;
};

const TrustCenter: React.FC<Props> = ({ dataStructureId, viewId, className }) => {
  const t = usePublicTranslation();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState('access');
  const [page, setPage] = useState<Page>(Page.RequestAccess);
  const [data, setData] = useState<TrustCenterData | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [code, setCode] = useState<string | undefined>(undefined);

  const {
    query: { id: trustCenterId }
  } = useRouter();

  const { data: trustCenterMainData } = useSWR<TrustCenterMainData>(
    textVariableReplacer(MAIN_DATA_URL, {
      dataStructureId,
      viewId,
      trustCenterId
    }),
    {
      onError: () => {
        router.push('/404').catch(() => {});
      }
    }
  );

  const handleTabClick = useCallback(({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
    const tab = currentTarget.getAttribute(dataAttributeTab);

    if (!tab) return;

    setActiveTab(tab);
  }, []);

  const handleRequestAccessSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!email) return;

      api
        .post(
          textVariableReplacer(ACCESS_REQUEST_URL, {
            dataStructureId,
            viewId,
            trustCenterId
          }),
          {
            email
          }
        )
        .then(() => {
          setPage(Page.CheckAccess);
        })
        .catch(() => {});
    },
    [dataStructureId, email, trustCenterId, viewId]
  );

  const handleCheckAccessSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!code) return;

      if (!email) {
        setPage(Page.RequestAccess);

        return;
      }
      api
        .put(
          textVariableReplacer(CHECK_ACCESS_URL, {
            dataStructureId,
            viewId,
            trustCenterId
          }),
          {
            email,
            code: parseInt(code, 10)
          }
        )
        .then(response => {
          setData(response.data);
          setPage(Page.AccessGranted);
          setEmail(undefined);
          setActiveTab('overview');
        })
        .catch(() => {});
    },
    [code, dataStructureId, email, trustCenterId, viewId]
  );

  const trustCenterData = useMemo<TrustCenterDataWithCategoryId | null>(
    () =>
      data
        ? {
            ...data,
            categories: data.categories.map(category => ({
              ...category,
              id: category.name.toLowerCase().replace(/\s+/g, '-'),
              // tasks: [...category.tasks].sort((a, b) => Number(b.completed) - Number(a.completed))
              tasks: [...category.tasks].sort((a, b) => {
                if (a.completed !== b.completed) {
                  return Number(b.completed) - Number(a.completed);
                }
                if (a.inProgress !== b.inProgress) {
                  return Number(b.inProgress) - Number(a.inProgress);
                }
                return Number(b.notStarted) - Number(a.notStarted);
              })
            }))
          }
        : null,
    [data]
  );

  const { data: settings } = useSWR<GeneralSettings>(`/setting/public/general/client`);

  const title = usePageTitle(
    t('trust_center.page_title', { organisation: trustCenterMainData?.organisationName || '' }),
    true,
    settings?.site_name
  );

  const logo = useMemo(() => {
    if (!trustCenterMainData?.organisationLogo) return undefined;

    return trustCenterMainData.organisationLogo.startsWith('/media/')
      ? trustCenterMainData.organisationLogo
      : `/files${trustCenterMainData.organisationLogo}`;
  }, [trustCenterMainData]);

  if (!trustCenterMainData) return null;

  return (
    <Fragment>
      <Head>
        <title>{title}</title>
      </Head>
      <div className={cn(className, styles.block, 'trust-center-block')}>
        <header className={cn(styles.header, 'header')}>
          <div className="container">
            <div className={cn(styles.logo, 'logo')}>
              {logo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={logo} alt={trustCenterMainData.organisationName} />
              ) : (
                <div className={cn(styles.logoPlaceholder, 'logo-placeholder')}>
                  {trustCenterMainData.organisationName}
                </div>
              )}
            </div>
          </div>
        </header>
        <div className={cn(styles.hero, 'hero')}>
          <div className="container">
            <div className={cn(styles.content, 'content')}>
              <h1
                dangerouslySetInnerHTML={{
                  __html: !!trustCenterMainData.organisationTitle.length
                    ? nl2br(trustCenterMainData.organisationTitle)
                    : t('trust_center.default_title')
                }}
              />
              <p
                dangerouslySetInnerHTML={{
                  __html: !!trustCenterMainData.organisationDescription.length
                    ? nl2br(trustCenterMainData.organisationDescription)
                    : t('trust_center.default_description')
                }}
              />
            </div>
          </div>
        </div>
        <div className={cn(styles.tabs, 'tabs')}>
          <div role="tablist">
            <div className="container">
              {page == Page.RequestAccess || page == Page.CheckAccess ? (
                <button
                  role="tab"
                  aria-selected={activeTab === 'access'}
                  aria-controls="access"
                  onClick={handleTabClick}
                  {...{ [dataAttributeTab]: 'access' }}
                >
                  {t('trust_center.access_by_invitation_tab')}
                </button>
              ) : (
                <Fragment>
                  <button
                    role="tab"
                    aria-selected={activeTab === 'overview'}
                    aria-controls="overview"
                    onClick={handleTabClick}
                    {...{ [dataAttributeTab]: 'overview' }}
                  >
                    {t('trust_center.overview_tab')}
                  </button>
                  {trustCenterData?.categories.map(category => (
                    <button
                      key={category.id}
                      role="tab"
                      aria-selected={activeTab === category.id}
                      aria-controls={category.id}
                      onClick={handleTabClick}
                      {...{ [dataAttributeTab]: category.id }}
                    >
                      {category.name}
                    </button>
                  ))}
                </Fragment>
              )}
            </div>
          </div>
          {page == Page.RequestAccess || page == Page.CheckAccess ? (
            <div
              role="tabpanel"
              id="access"
              className={cn(styles.tabPanel, 'tab-panel')}
              hidden={activeTab !== 'access'}
            >
              <div className="container">
                <div className={cn(styles.tabContent, styles.accessTabContent, 'tab-content', 'access-tab-content')}>
                  {page == Page.RequestAccess && (
                    <Fragment>
                      <h2 dangerouslySetInnerHTML={{ __html: t('trust_center.access_by_invitation_request_title') }} />
                      <p dangerouslySetInnerHTML={{ __html: t('trust_center.access_by_invitation_request_text') }} />
                      <form className={cn(styles.form, 'form')} onSubmit={handleRequestAccessSubmit}>
                        <input
                          type="email"
                          onChange={e => setEmail(e.currentTarget.value)}
                          placeholder={t('trust_center.access_by_invitation_email_placeholder')}
                        />
                        <button type="submit">{t('trust_center.access_by_invitation_request_access_button')}</button>
                      </form>
                    </Fragment>
                  )}
                  {page == Page.CheckAccess && (
                    <Fragment>
                      <h2 dangerouslySetInnerHTML={{ __html: t('trust_center.access_by_invitation_check_title') }} />
                      <p dangerouslySetInnerHTML={{ __html: t('trust_center.access_by_invitation_check_text') }} />
                      <form className={cn(styles.form, 'form')} onSubmit={handleCheckAccessSubmit}>
                        <input
                          type="code"
                          placeholder={t('trust_center.access_by_invitation_code_placeholder')}
                          onChange={e => setCode(e.currentTarget.value)}
                        />
                        <button type="submit">{t('trust_center.access_by_invitation_request_access_button')}</button>
                      </form>
                    </Fragment>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Fragment>
              <div
                role="tabpanel"
                id="overview"
                className={cn(styles.tabPanel, 'tab-panel')}
                hidden={activeTab !== 'overview'}
              >
                <div className="container">
                  <div
                    className={cn(styles.tabContent, styles.overviewTabContent, 'tab-content', 'overview-tab-content')}
                  >
                    <div className={cn(styles.overviewTabContentHead, 'overview-tab-content-head')}>
                      {data && (
                        <div className={cn(styles.overviewLevel, 'overview-level')}>
                          {t('trust_center.level', { name: data.level })}
                        </div>
                      )}
                      {data && <h2 dangerouslySetInnerHTML={{ __html: data.levelTitle }} />}
                    </div>
                    <div className={cn(styles.overviewTabContentBody, 'overview-tab-content-body')}>
                      <div>
                        {data && (
                          <div
                            className={cn(styles.levelText, 'level-text')}
                            dangerouslySetInnerHTML={{ __html: nl2br(data.levelText) }}
                          />
                        )}
                      </div>
                      <div>
                        {
                          // eslint-disable-next-line @next/next/no-img-element
                          data && <img src={data.levelImage} alt={''} />
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {trustCenterData?.categories.map(category => (
                <div
                  key={category.id}
                  role="tabpanel"
                  id={category.id}
                  className={cn(styles.tabPanel, 'tab-panel')}
                  hidden={activeTab !== category.id}
                >
                  <div className="container">
                    <div
                      className={cn(
                        styles.tabContent,
                        styles.categoryTabContent,
                        'tab-content',
                        'category-tab-content'
                      )}
                    >
                      <ul className="fa-ul">
                        {category.tasks.map((task, index) => (
                          <li
                            key={index}
                            className={cn({
                              [styles.completed]: task.completed,
                              ['completed']: task.completed,
                              [styles.inProgress]: task.inProgress,
                              ['in-progress']: task.inProgress,
                              [styles.notStarted]: task.notStarted,
                              ['not-started']: task.notStarted
                            })}
                          >
                            <span className="fa-li">
                              <i
                                className={cn(
                                  {
                                    ['fa-solid']: task.completed || task.inProgress,
                                    ['fa-regular']: task.notStarted
                                  },
                                  {
                                    ['fa-circle-check']: task.completed,
                                    ['fa-spinner']: task.inProgress,
                                    ['fa-circle-dot']: task.notStarted
                                  }
                                )}
                              ></i>
                            </span>
                            {task.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </Fragment>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default TrustCenter;
